'use client';

import { FirebaseError } from 'firebase/app';
import {
  AuthError,
  getIdToken,
  onIdTokenChanged,
  signInWithEmailAndPassword,
  signOut,
  User as FirebaseUser,
} from 'firebase/auth';
import { documentId, query, where } from 'firebase/firestore';

import { getCurrentMessages } from '@/i18n/get-messages';
import { AppError } from '@/lib/error';
import { getCollection, getDocument, onDataSnapshot, updateData } from '@/lib/firebase';
import { firebaseAuth } from '@/lib/firebase';
import { getUserMessage, shouldLogErrorMessage } from '@/lib/firebase-auth';
import { rollbar } from '@/lib/rollbar';

import {
  AuthAdapter,
  AuthCapabilities,
  AuthFlowType,
  AuthState,
  AuthUser,
  CredentialsInput,
  InviteInfo,
  UserPreferences,
  VerifyPasswordResetInput,
} from './auth-adapter';

type FirestoreProfile = {
  id: string;
  email?: string;
  name?: string;
  avatar?: string;
  role?: string;
  roles?: string[];
};

type FirestoreRole = {
  id: string;
  rules: string[];
};

function mapToAuthUser(
  firebaseUser: FirebaseUser,
  profile: FirestoreProfile,
  permissions: string[],
  preferences: UserPreferences,
): AuthUser {
  return {
    id: firebaseUser.uid,
    email: profile.email ?? firebaseUser.email ?? undefined,
    name: profile.name ?? firebaseUser.displayName ?? undefined,
    avatar: profile.avatar ?? firebaseUser.photoURL ?? undefined,
    role: profile.role,
    roles: profile.roles ?? [],
    permissions,
    preferences,
  };
}

export class FirebaseAdapter extends AuthAdapter {
  readonly name = 'Firebase';
  readonly flowType: AuthFlowType = 'credentials';
  readonly capabilities: AuthCapabilities = {
    canSignInWithCredentials: true,
    canSignUp: false,
    canResetPassword: false,
    canChangePassword: false,
    canAcceptInvite: true,
  };

  private currentState: AuthState = { user: null, token: null, status: 'idle', error: null };
  private listeners: Set<(state: AuthState) => void> = new Set();
  private currentFirebaseUser: FirebaseUser | null = null;
  private currentProfile: FirestoreProfile | null = null;
  private rolesMap: Map<string, string[]> | null = null;
  private currentPreferences: UserPreferences = {};
  private unsubscribeProfile: (() => void) | null = null;
  private unsubscribeRoles: (() => void) | null = null;
  private unsubscribePreferences: (() => void) | null = null;

  constructor() {
    super();
    this.initialize = this.initialize.bind(this);
    this.getAuthState = this.getAuthState.bind(this);
    this.getAccessToken = this.getAccessToken.bind(this);
    this.signIn = this.signIn.bind(this);
    this.signUp = this.signUp.bind(this);
    this.signOut = this.signOut.bind(this);
    this.resetPassword = this.resetPassword.bind(this);
    this.verifyResetPasswordCode = this.verifyResetPasswordCode.bind(this);
    this.completePasswordReset = this.completePasswordReset.bind(this);
    this.changePassword = this.changePassword.bind(this);
    this.onAuthStateChange = this.onAuthStateChange.bind(this);
    this.acceptInvite = this.acceptInvite.bind(this);
    this.checkInvite = this.checkInvite.bind(this);
    this.resetErrors = this.resetErrors.bind(this);
    this.updatePreferences = this.updatePreferences.bind(this);
  }

  private updateState(newState: AuthState): void {
    this.currentState = newState;
    this.listeners.forEach((listener) => {
      listener(newState);
    });
  }

  private computePermissions(): string[] {
    if (!this.rolesMap) return [];

    const roleIds = this.currentProfile?.roles ?? [];
    const permissions = new Set<string>();
    for (const roleId of roleIds) {
      const rules = this.rolesMap.get(roleId);
      if (rules) for (const rule of rules) permissions.add(rule);
    }
    return Array.from(permissions);
  }

  private async buildAuthenticatedState(): Promise<AuthState> {
    const firebaseUser = this.currentFirebaseUser;
    const profile = this.currentProfile;
    const roles = this.rolesMap;

    if (!firebaseUser || !profile || !roles) {
      return { user: null, token: null, status: 'loading', error: null };
    }

    const token = await getIdToken(firebaseUser);

    return {
      user: mapToAuthUser(
        firebaseUser,
        profile,
        this.computePermissions(),
        this.currentPreferences,
      ),
      token: { accessToken: token, tokenType: 'Bearer' },
      status: 'authenticated',
      error: null,
    };
  }

  private subscribeToRoles(roleIds: string[]): void {
    this.cleanupRolesListener();

    if (roleIds.length === 0) {
      this.rolesMap = new Map();
      return;
    }

    const rolesQuery = query(
      getCollection<FirestoreRole>('roles'),
      where(documentId(), 'in', roleIds),
    );

    this.unsubscribeRoles = onDataSnapshot(
      rolesQuery,
      async (roles) => {
        this.rolesMap = new Map(roles.map((r) => [r.id, r.rules]));
        if (this.currentFirebaseUser && this.currentProfile) {
          const state = await this.buildAuthenticatedState();
          this.updateState(state);
        }
      },
      (error) => {
        rollbar.error('Failed to listen to roles', error);
      },
    );
  }

  private cleanupRolesListener(): void {
    if (this.unsubscribeRoles) {
      this.unsubscribeRoles();
      this.unsubscribeRoles = null;
    }
    this.rolesMap = null;
  }

  private subscribeToProfile(uid: string): void {
    this.cleanupProfileListener();

    const docRef = getDocument<FirestoreProfile>('users', uid);

    this.unsubscribeProfile = onDataSnapshot(
      docRef,
      async (firestoreUser) => {
        if (firestoreUser) {
          const prevRoles = JSON.stringify([...(this.currentProfile?.roles ?? [])].sort());
          this.currentProfile = firestoreUser as FirestoreProfile;
          const newRoles = JSON.stringify([...(firestoreUser.roles ?? [])].sort());
          if (!this.rolesMap || prevRoles !== newRoles) {
            this.subscribeToRoles(firestoreUser.roles ?? []);
          }
        } else {
          this.currentProfile = null;
          rollbar.warning('User authenticated but has no Firestore profile', { uid });
        }

        if (this.currentFirebaseUser && this.rolesMap) {
          const state = await this.buildAuthenticatedState();
          this.updateState(state);
        }
      },
      (error) => {
        rollbar.error('Failed to listen to user profile', error);
        this.currentProfile = null;
      },
    );
  }

  private cleanupProfileListener(): void {
    if (this.unsubscribeProfile) {
      this.unsubscribeProfile();
      this.unsubscribeProfile = null;
    }
    this.currentProfile = null;
  }

  private subscribeToPreferences(uid: string): void {
    this.cleanupPreferencesListener();

    const docRef = getDocument<UserPreferences>('users', uid, 'data', 'preferences');

    this.unsubscribePreferences = onDataSnapshot(
      docRef,
      async (data) => {
        this.currentPreferences = data ?? {};
        if (this.currentFirebaseUser && this.currentProfile && this.rolesMap) {
          const state = await this.buildAuthenticatedState();
          this.updateState(state);
        }
      },
      (error) => {
        rollbar.error('Failed to listen to user preferences', error);
      },
    );
  }

  private cleanupPreferencesListener(): void {
    if (this.unsubscribePreferences) {
      this.unsubscribePreferences();
      this.unsubscribePreferences = null;
    }
    this.currentPreferences = {};
  }

  async initialize(): Promise<void> {
    this.updateState({ ...this.currentState, status: 'loading' });

    onIdTokenChanged(firebaseAuth, async (firebaseUser) => {
      if (!firebaseUser) {
        this.currentFirebaseUser = null;
        this.cleanupProfileListener();
        this.cleanupRolesListener();
        this.updateState({ user: null, token: null, status: 'unauthenticated', error: null });
        return;
      }

      this.currentFirebaseUser = firebaseUser;
      this.subscribeToProfile(firebaseUser.uid);
      this.subscribeToPreferences(firebaseUser.uid);

      // Only update immediately if we already have the profile cached
      if (this.currentProfile && this.rolesMap) {
        const state = await this.buildAuthenticatedState();
        this.updateState(state);
      }
    });
  }

  getAuthState(): AuthState {
    return this.currentState;
  }

  async getAccessToken(): Promise<string | null> {
    const firebaseUser = firebaseAuth.currentUser;
    if (!firebaseUser) return null;
    return getIdToken(firebaseUser);
  }

  onAuthStateChange(callback: (state: AuthState) => void): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  protected async signInWithRedirect(): Promise<void> {
    throw new Error('Redirect sign-in is not supported by FirebaseAdapter');
  }

  protected async signInWithCredentials(credentials: CredentialsInput): Promise<AuthState> {
    this.updateState({ ...this.currentState, status: 'loading', error: null });

    try {
      await signInWithEmailAndPassword(firebaseAuth, credentials.email, credentials.password);
      // State will be updated via onAuthStateChanged + onSnapshot
      return this.currentState;
    } catch (err) {
      let error: AppError;
      if (err instanceof FirebaseError) {
        const errorMessage = await getUserMessage(err as AuthError);
        const shouldLog = shouldLogErrorMessage(err as AuthError);
        error = new AppError('internalError', 'Failed to sign in', {
          cause: err,
          skipLogging: !shouldLog,
          details: {
            root: [errorMessage],
          },
        });
      } else {
        const messages = await getCurrentMessages();
        error = new AppError('internalError', 'Failed to sign in', {
          cause: err,
          details: {
            root: [messages.auth.errors.unexpected],
          },
        });
      }

      const state: AuthState = { user: null, token: null, status: 'error', error };
      this.updateState(state);
      return state;
    }
  }

  async signUp(_credentials: CredentialsInput & { userId: string }): Promise<AuthState> {
    throw new Error('Sign up is not supported');
  }

  async checkInvite(userId: string): Promise<InviteInfo> {
    this.updateState({ status: 'loading', error: null, user: null, token: null });
    try {
      const response = await fetch(`/api/auth/invite/${userId}`);

      if (response.status === 404) throw new AppError('notFound', 'Convite inválido');
      if (!response.ok) throw new AppError('internalError', 'Failed to check invite');

      const invite = await response.json();
      this.updateState({ status: 'unauthenticated', error: null, user: null, token: null });
      return invite;
    } catch (error) {
      const appError =
        error instanceof AppError
          ? error
          : new AppError('internalError', 'Failed to check invite', { cause: error });
      if (appError.shouldLog()) {
        rollbar.error('Failed to check invite', error as Error, { userId });
      }
      this.updateState({ status: 'error', error: appError, user: null, token: null });
      throw appError;
    }
  }

  async acceptInvite(userId: string, password: string, email: string): Promise<void> {
    this.updateState({ status: 'loading', error: null, user: null, token: null });
    try {
      const response = await fetch(`/api/users/${userId}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });

      if (response.status === 404) throw new AppError('notFound', 'Convite inválido');
      if (response.status === 409) throw new AppError('conflict', 'Este convite já foi aceito.');
      if (!response.ok) {
        const err = await response.json().catch(() => null);
        throw new AppError(
          'internalError',
          `Failed to register user: ${err?.message ?? response.statusText}`,
          { details: { response: err } },
        );
      }

      await this.signIn({ email, password });
    } catch (error) {
      const appError =
        error instanceof AppError
          ? error
          : new AppError('internalError', 'Failed to register user', { cause: error });
      if (appError.shouldLog()) {
        rollbar.error('Failed to register user', error as Error, { userId });
      }
      this.updateState({ status: 'error', error: appError, user: null, token: null });
      throw appError;
    }
  }

  async signOut(): Promise<void> {
    this.cleanupProfileListener();
    this.cleanupRolesListener();
    this.cleanupPreferencesListener();
    await signOut(firebaseAuth);
  }

  async resetPassword(email: string): Promise<void> {
    this.updateState({ status: 'loading', user: null, token: null, error: null });
    try {
      const response = await fetch(`/api/auth/password/recovery`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (response.status === 404) throw new AppError('notFound', 'E-mail não encontrado');
      if (!response.ok) {
        const err = await response.json().catch(() => null);
        throw new AppError(
          'internalError',
          `Failed to reset password: ${err?.message ?? response.statusText}`,
        );
      }
      this.updateState({ status: 'unauthenticated', user: null, token: null, error: null });
    } catch (error) {
      const appError =
        error instanceof AppError
          ? error
          : new AppError('internalError', 'Failed to reset password', { cause: error });
      if (appError.shouldLog()) {
        rollbar.error('Failed to reset password', error as Error, { email });
      }
      this.updateState({ status: 'error', error: appError, user: null, token: null });
      throw appError;
    }
  }

  async verifyResetPasswordCode(input: VerifyPasswordResetInput): Promise<string> {
    this.updateState({ status: 'loading', error: null, user: null, token: null });
    try {
      const response = await fetch(`/api/auth/password/recovery/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });

      if (response.status === 404) throw new AppError('notFound', 'Código de recuperação inválido');
      if (!response.ok) {
        const err = await response.json().catch(() => null);
        throw AppError.fromJSON(err, 'Failed to verify code');
      }

      const data = await response.json();
      this.updateState({ status: 'unauthenticated', user: null, token: null, error: null });
      return data.email as string;
    } catch (error) {
      const appError =
        error instanceof AppError
          ? error
          : new AppError('internalError', 'Failed to verify code', { cause: error });
      if (appError.shouldLog()) {
        rollbar.error('Failed to verify code', error as Error, { input });
      }
      this.updateState({ status: 'error', error: appError, user: null, token: null });
      throw appError;
    }
  }

  async completePasswordReset(email: string, code: string, newPassword: string): Promise<void> {
    this.updateState({ status: 'loading', error: null, user: null, token: null });
    try {
      const response = await fetch(`/api/auth/password/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code, newPassword }),
      });

      if (response.status === 404) throw new AppError('notFound', 'Código de recuperação inválido');
      if (!response.ok) {
        const err = await response.json().catch(() => null);
        throw new AppError(
          'internalError',
          `Failed to reset password: ${err?.message ?? response.statusText}`,
          { details: { response: err } },
        );
      }
      this.updateState({ status: 'unauthenticated', error: null, user: null, token: null });
    } catch (error) {
      const appError =
        error instanceof AppError
          ? error
          : new AppError('internalError', 'Failed to reset password', { cause: error });
      if (appError.shouldLog()) {
        rollbar.error('Failed to reset password', error as Error, { userId: email, code });
      }
      this.updateState({ status: 'error', error: appError, user: null, token: null });
      throw appError;
    }
  }

  async changePassword(_oldPassword: string, _newPassword: string): Promise<void> {
    throw new Error('Change password is not supported');
  }

  async updatePreferences(prefs: Partial<UserPreferences>): Promise<void> {
    const firebaseUser = this.currentFirebaseUser;
    if (!firebaseUser) throw new AppError('internalError', 'Not authenticated');

    this.currentPreferences = { ...this.currentPreferences, ...prefs };
    await updateData<UserPreferences>(['users', firebaseUser.uid, 'data'], 'preferences', prefs);
  }

  resetErrors(): void {
    this.updateState({ ...this.currentState, error: null });
  }
}
